import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';
import { initStore, purchasePremium, restorePurchases } from '../services/purchase';

const PremiumContext = createContext(null);

export const FREE_LIMITS = {
  locations: 4,                // max 4 hylder per lagerenhed
  storage_units_per_type: 1,   // max 1 fryser, 1 køleskab, 1 skab (3 i alt)
  households: 1,
};

export function PremiumProvider({ children }) {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading]     = useState(true);

  // ── Hent premium-status fra backend ────────────────────────────────────────
  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const status = await api.getPremiumStatus();
      setIsPremium(status.isPremium);
    } catch {
      setIsPremium(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ── Callback når IAP-plugin melder et gennemført køb ───────────────────────
  // Selve købet er allerede verificeret + registreret server-side via
  // store.validator (→ /api/premium/validate). Her opdaterer vi blot status.
  const onNativePurchased = useCallback(async () => {
    await load();
  }, [load]);

  // ── Initialiser IAP-store ved første load ───────────────────────────────────
  useEffect(() => {
    initStore(onNativePurchased).catch(err =>
      console.warn('initStore fejl:', err)
    );
  }, [onNativePurchased]);

  useEffect(() => { load(); }, [load]);

  // ── Køb premium (kaldes fra PaywallModal) ───────────────────────────────────
  const purchase = async (productId) => {
    const result = await purchasePremium(productId);

    if (result.platform === 'web') {
      // Køb kræver App Store/Google Play på rigtig enhed
      throw new Error('Køb er kun tilgængeligt i mobil-appen');
    }
    // Native: store.validator validerer + registrerer server-side, derefter
    // opdaterer onNativePurchased status via store.when().finished()
  };

  // ── Gendan tidligere køb ────────────────────────────────────────────────────
  const restore = async () => {
    // store.restorePurchases() løser sin promise INDEN validator-kæden er færdig
    // (approved → verify → /validate → finished). Vi poller backend i op til 15s
    // så brugeren ikke ser "ingen køb fundet" mens valideringen stadig kører.
    await restorePurchases();
    for (let i = 0; i < 15; i++) {
      await new Promise(r => setTimeout(r, 1000));
      try {
        const status = await api.getPremiumStatus();
        if (status.isPremium) { setIsPremium(true); return true; }
      } catch {}
    }
    return false;
  };

  const deactivate = async () => {
    await api.deactivatePremium();
    setIsPremium(false);
  };

  // Dev-hjælpere eksponeres KUN i development — aldrig i produktion
  const devTools = import.meta.env.DEV ? {
    activate: async () => { await api.devActivatePremium(); setIsPremium(true); },
    deactivate,
  } : {};

  return (
    <PremiumContext.Provider value={{
      isPremium,
      loading,
      purchase,
      restore,
      reload: load,
      ...devTools,
    }}>
      {children}
    </PremiumContext.Provider>
  );
}

export const usePremium = () => {
  const ctx = useContext(PremiumContext);
  if (!ctx) throw new Error('usePremium skal bruges inden i PremiumProvider');
  return ctx;
};
